<template>
	<div>
		<div>
			<CInput
				v-model.trim="name"
				placeholder="Search by page widget name"
				class="flex-fill"
				autocomplete="off"
				data-test-id="input-page-widget-name"
			>
				<template #prepend-content>
					<CIcon class="cil-magnifying-glass" name="cil-magnifying-glass" />
				</template>
			</CInput>
		</div>
		<slot
			v-if="!filteredList.length"
			name="no-item"
		>
		</slot>
		<div v-show="filteredList.length" class="list">
			<div class="select-all mb-1 align-items-center">
				<CInputCheckbox
					:checked.sync="isSelectedAll"
					custom
					label="Select all"
					class="select-all-input"
					add-label-classes="font-weight-bold"
					data-test-id="select-all-input"
					@update:checked="handleCheckedSelectAll"
				/>
			</div>
			<DynamicScroller
				:items="filteredList"
				:min-item-size="35"
				key-field="id"
				class="scroller list-select"
			>
				<template v-slot="{ item, index, active }">
					<DynamicScrollerItem
						:item="item"
						:active="active"
						:data-index="index"
						class="list-item"
					>
						<div class="d-flex flex-fill">
							<CInputCheckbox
								:id="`static_id_${item.id}`"
								:checked="checkItemIsSelected(item.id)"
								custom
								class="list-checkbox overflow-hidden"
								@update:checked="handleChecked($event, item)"
							>
								<template #label>
									<label :for="`static_id_${item.id}`" class="custom-control-label list-label w-100 typo-body-2 font-weight-normal text-nowrap text-truncate color-black-45 mb-0 ml-2">
										<span class="list-page color-black">{{ item.name }}</span>
									</label>
								</template>
							</CInputCheckbox>
						</div>
					</DynamicScrollerItem>
				</template>
			</DynamicScroller>
		</div>
	</div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller';
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';

export default {
	name: 'FormGetStaticPage',
	components: {
		DynamicScroller,
		DynamicScrollerItem,
	},
	props: {
		selectedList: {
			type: Array,
			default: () => [],
		},
	},
	data() {
		return {
			isSelectedAll: false,
			name: '',
			pageWidgetList: [],
		};
	},
	computed: {
		...mapState('pages', {
			pageWidgets: 'pageWidgets',
		}),

		filteredList() {
			if (!this.name) {
				return this.pageWidgetList;
			}

			return this.pageWidgetList.filter((pageWidget) => pageWidget.name.toLowerCase().startsWith(this.name.toLowerCase()));
		},

		initalPageList() {
			return this.pageWidgets.data;
		},
	},
	watch: {
		filteredList() {
			this.checkSelectionState();
		},

		selectedList() {
			this.checkSelectionState();
		},
	},
	async created() {
		await this.getPageWidgets();

		this.pageWidgetList = this.initalPageList;
	},
	methods: {
		...mapActions({
			getPageWidgets: 'pages/getPageWidgetsProduct',
		}),
		resetState() {
			this.name = '';
		},
		checkItemIsSelected(val) {
			return this.selectedList.some((item) => item.id === val);
		},
		checkSelectionState() {
			const isEverySelected = this.filteredList.length && this.filteredList.every((page) => this.selectedList.map((item) => item.id).includes(page.id));
			const isSomeSelected = this.filteredList.length && this.filteredList.some((page) => this.selectedList.map((item) => item.id).includes(page.id));

			if (isEverySelected) {
				this.setSelectedAllState('checked');
				return;
			}

			if (isSomeSelected) {
				this.setSelectedAllState('indeterminated');
				return;
			}

			this.setSelectedAllState('unchecked');
		},
		setSelectedAllState(state) {
			let isChecked = false;
			let isIndeterminate = false;
			switch (state) {
				case 'checked': {
					isChecked = true;
					isIndeterminate = false;
					break;
				}
				case 'unchecked': {
					isChecked = false;
					isIndeterminate = false;
					break;
				}
				case 'indeterminated': {
					isChecked = true;
					isIndeterminate = true;
					break;
				}
				default: break;
			}

			const selectAllEle = this.$el.querySelector('.select-all-input input');
			if (selectAllEle) {
				selectAllEle.checked = isChecked;
				selectAllEle.indeterminate = isIndeterminate;
			}
		},
		handleCheckedSelectAll() {
			if (this.isSelectedAll) {
				const selectedItems = this.filteredList;
				this.addToSelectedList(selectedItems);
			} else {
				const selectedItems = this.filteredList.map((item) => item.id);
				this.removeFromSelectedList(selectedItems);
			}
		},
		handleChecked(checked, page) {
			const isSelected = this.selectedList.some((item) => item.id === page.id);
			if (!isSelected) {
				this.addToSelectedList(page);
			} else {
				this.removeFromSelectedList(page.id);
			}
		},

		/**
		 * @param {String|Array} items page or List of page
		 */
		addToSelectedList(items = []) {
			let newItems = items;
			if (!Array.isArray(items)) {
				newItems = [items];
			}
			const uniqueItems = [...new Set([...this.selectedList, ...newItems])].sort();
			this.$emit('onChange', uniqueItems);
		},

		/**
		 * @param {String|Array} items page or List of widgets
		 */
		removeFromSelectedList(items = []) {
			let itemsToRemove = items;
			if (!Array.isArray(items)) {
				itemsToRemove = [items];
			}

			this.$emit('onChange', this.selectedList.filter((selectedItem) => !itemsToRemove.includes(selectedItem.id)));
		},
	},
};
</script>

<style lang="scss" scoped>
.scroller {
	height: 100%;
}

.btn-add {
	min-width: 84px;
}

.list {
	margin: 0 -16px -16px;
}

.select-all {
	background-color: $color-gray-100;
	padding: 8px 16px;
}

.list-select {
	min-height: 360px;
	max-height: 360px;
	list-style: none;
	padding: 0;
	margin: 0;

	.list-item {
		padding: 6px 16px;

		label {
			margin-top: 3px;
			cursor: pointer;
		}
	}
}
</style>
